import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { callAjaxIO } from 'Shared/ajax_io';
import { apiClient } from 'Shared/api_client';
import type { StepPayload } from './step_payload';
import type { ApiGuide, ApiGuideItem, ApiStep, BootPrerequisite, GuideVideoInfo } from './types';

/**
 * A stale-revisionid rejection. The server's 409 body is the current
 * object, so conflict UIs can offer it instead of a dead-end retry loop.
 */
export class StepConflictError extends Error {
   readonly serverStep: ApiStep;

   constructor(serverStep: ApiStep) {
      super('The step changed since it was loaded.');
      this.serverStep = serverStep;
   }
}

function throwStepConflictOn409(error: unknown): never {
   const response = (error as { response?: { status?: number; data?: ApiStep } }).response;
   if (response?.status === 409 && response.data?.revisionid) {
      throw new StepConflictError(response.data);
   }
   throw error;
}

const guideEditQueryKey = (guideid: number) => ['guide-edit', guideid] as const;

/**
 * The head revision including unpatrolled edits.
 * Prerequisite steps are excluded because they belong to other guides and
 * aren't editable here.
 */
export function useGuideQuery(guideid: number, langid: string) {
   return useQuery({
      queryKey: guideEditQueryKey(guideid),
      queryFn: async () => {
         const response = await apiClient.get<ApiGuide>(
            `guides/${guideid}?langid=${langid}&unpatrolled&excludePrerequisiteSteps`
         );
         return response.data;
      },
      // Editing surface: a background refetch can't clobber form state (the
      // forms seed once), but it causes pointless churn mid-edit.
      refetchOnWindowFocus: false,
   });
}

/**
 * Unfiltered prerequisite DB truth for the guide revision that just arrived.
 * The public guide GET omits prerequisites the editor cannot view, so revision
 * reconciliation cannot safely advance without this separate edit-only read.
 */
export function useGuidePrerequisites(
   guideid: number,
   langid: string,
   revisionid: string,
   enabled: boolean
) {
   return useQuery({
      queryKey: ['guide-edit-prerequisites', guideid, langid, revisionid] as const,
      enabled,
      staleTime: Number.POSITIVE_INFINITY,
      queryFn: () => callAjaxIO<BootPrerequisite[]>('getV2Prerequisites', guideid, langid),
   });
}

type ApiHostedVideo = {
   videoid?: number;
   image?: {
      image?: { thumbnail?: string; standard?: string } | null;
      thumbnail?: string;
      standard?: string;
   } | null;
   /** Present only on a just-picked Media Manager cache seed. */
   thumbnail?: string | null;
   encodings?: { column?: string; url?: string }[];
};

type HostedVideo = {
   thumbnail: string | null;
   stepVideo: GuideVideoInfo | null;
};

/**
 * Poster and playable encodings for a hosted video. Intro and Steps consume
 * the same query so selecting a new upload does not duplicate the media read.
 */
export function useHostedVideo(videoid: number | null) {
   return useQuery({
      queryKey: hostedVideoKey(videoid ?? 0),
      enabled: videoid !== null,
      staleTime: 5 * 60_000,
      queryFn: async () => {
         const response = await apiClient.get<ApiHostedVideo>(`media/videos/${videoid}`);
         return response.data;
      },
      select: toHostedVideo,
   });
}

const hostedVideoKey = (videoid: number) => ['media-hosted-video', videoid] as const;

/** Prime the shared hosted-video query from a picked Media Manager item. */
export function seedVideoThumbnail(
   queryClient: ReturnType<typeof useQueryClient>,
   videoid: number,
   thumbnail: string | null
) {
   if (queryClient.getQueryData(hostedVideoKey(videoid)) !== undefined) {
      return;
   }
   // updatedAt 0 makes the partial seed immediately stale. The thumbnail can
   // render now while the playable encodings are fetched for the Steps tab.
   queryClient.setQueryData<ApiHostedVideo>(
      hostedVideoKey(videoid),
      { videoid, thumbnail },
      { updatedAt: 0 }
   );
}

function toHostedVideo(video: ApiHostedVideo): HostedVideo {
   const image = video.image?.image ?? video.image;
   const videoUrls: NonNullable<GuideVideoInfo['videoUrls']> = {};
   for (const encoding of video.encodings ?? []) {
      if (!encoding.url) {
         continue;
      }
      switch (encoding.column?.toLowerCase()) {
         case 'mp4_720': {
            videoUrls.mp4_720 = encoding.url;
            break;
         }
         case 'mp4_592': {
            videoUrls.mp4_592 = encoding.url;
            break;
         }
         case 'webm': {
            videoUrls.webm = encoding.url;
            break;
         }
         case 'ogg': {
            videoUrls.ogg = encoding.url;
            break;
         }
      }
   }
   return {
      thumbnail: video.thumbnail ?? image?.thumbnail ?? image?.standard ?? null,
      stepVideo:
         video.videoid !== undefined && Object.keys(videoUrls).length > 0
            ? { type: 'ifixit', videoId: String(video.videoid), videoUrls }
            : null,
   };
}

/**
 * Guide-level PATCH: intro and details fields. The response is the full
 * updated guide, so it reseeds the query directly. A 409 (stale revisionid)
 * rejects with the current guide in `error.response.data`.
 */
export function useSaveGuide(guideid: number) {
   const queryClient = useQueryClient();
   return useMutation({
      mutationFn: async (args: { revisionid: string; patch: Record<string, unknown> }) => {
         const response = await apiClient.patch(
            `guides/${guideid}?revisionid=${encodeURIComponent(args.revisionid)}`,
            args.patch
         );
         return response.data as ApiGuide;
      },
      onSuccess: guide => {
         queryClient.setQueryData(guideEditQueryKey(guideid), guide);
      },
   });
}

/**
 * Whole-guide delete (soft delete server-side). Revision-gated like the
 * guide PATCH: a stale revisionid rejects 409 with the current guide in
 * `error.response.data`, and a lost privilege rejects 403 — the caller
 * surfaces either. Success reseeds nothing because the page navigates away.
 */
export function useDeleteGuide(guideid: number) {
   return useMutation({
      mutationFn: async (args: { revisionid: string }) => {
         await apiClient.delete(
            `guides/${guideid}?revisionid=${encodeURIComponent(args.revisionid)}`
         );
      },
   });
}

/**
 * A just-picked prerequisite guide's own data, for the import-on-add offer:
 * its prerequisites (their steps inline into this guide only if listed here
 * directly, since prereq inlining is one level deep) and its tools and parts.
 * Failures resolve to no offer — the picked prereq itself is already added,
 * so there's nothing to retry into.
 */
export function usePrereqImportGuide(guideid: number | null, langid: string) {
   return useQuery({
      queryKey: ['prereq-import', guideid, langid] as const,
      enabled: guideid !== null,
      retry: 1,
      refetchOnWindowFocus: false,
      // Within a session a guide's prereqs/items barely move; re-picking the
      // same guide (remove, re-add) shouldn't refetch.
      staleTime: 60_000,
      queryFn: async () => {
         const response = await apiClient.get<ApiGuide>(
            `guides/${guideid}?langid=${langid}&excludePrerequisiteSteps`
         );
         return response.data;
      },
   });
}

/**
 * Every tool the listed prerequisites use, in the order they are listed:
 * getPrereqItems orders a tool by the first prerequisite it shows up on.
 */
export function usePrereqTools(guideid: number, langid: string, prereqGuideids: number[]) {
   return useQuery({
      queryKey: ['prereq-tools', guideid, langid, prereqGuideids] as const,
      enabled: prereqGuideids.length > 0,
      retry: 1,
      // Focus is the only refetch left: this tab is force-mounted, so mount
      // fires once a page load, and fixing a prereq elsewhere is the case.
      staleTime: 30_000,
      queryFn: () =>
         callAjaxIO<ApiGuideItem[]>('getV2PrereqTools', guideid, langid, prereqGuideids),
   });
}

export type SaveStepArgs = {
   /** null creates a new step (POST); otherwise PATCH with its revisionid. */
   stepid: number | null;
   revisionid?: string;
   payload: StepPayload;
};

/**
 * Per-step save through the step endpoints. On stale revisionid the
 * server responds 409 with the current step as the body — the caller reads
 * it from `error.response.data` to drive the conflict flow.
 */
export function useSaveStep(guideid: number) {
   const queryClient = useQueryClient();
   return useMutation({
      mutationFn: async ({ stepid, revisionid, payload }: SaveStepArgs) => {
         try {
            if (stepid === null) {
               const response = await apiClient.post(`guides/${guideid}/steps`, payload);
               return response.data;
            }
            const response = await apiClient.patch(
               `guides/${guideid}/steps/${stepid}?revisionid=${encodeURIComponent(revisionid ?? '')}`,
               payload
            );
            return response.data;
         } catch (error) {
            throwStepConflictOn409(error);
         }
      },
      onError: (_error, variables) => {
         if (variables.stepid === null) {
            // A create posts an orderby computed from the cached step count;
            // if it 422s (another session changed the steps) refetch so the
            // retry doesn't resend the same stale position forever. The
            // new-step editor is keyed independently of the guide, so the
            // refetch doesn't discard what's typed.
            void queryClient.invalidateQueries({ queryKey: guideEditQueryKey(guideid) });
         }
      },
      onSuccess: (data, variables) => {
         if (variables.stepid === null) {
            // POST returns the whole updated guide; seeding the cache with
            // it (instead of refetching) means the new step is selectable
            // the moment onCreated fires.
            queryClient.setQueryData(guideEditQueryKey(guideid), data as ApiGuide);
            return;
         }
         // PATCH returns just the step; merge it into the cached guide so
         // the rail updates without a full refetch. A plain step save
         // doesn't change the guide-level revisionid, and refetching here
         // remounted the editor mid-typing (keystrokes between save and
         // refetch were silently discarded).
         const saved = data as ApiStep;
         queryClient.setQueryData(guideEditQueryKey(guideid), (cached: ApiGuide | undefined) =>
            cached
               ? {
                    ...cached,
                    steps: cached.steps.map(step => (step.stepid === saved.stepid ? saved : step)),
                 }
               : cached
         );
      },
   });
}

export function useDeleteStep(guideid: number) {
   const queryClient = useQueryClient();
   return useMutation({
      mutationFn: async (args: { stepid: number; revisionid: string }) => {
         try {
            const response = await apiClient.delete(
               `guides/${guideid}/steps/${args.stepid}?revisionid=${encodeURIComponent(args.revisionid)}`
            );
            return response.data;
         } catch (error) {
            throwStepConflictOn409(error);
         }
      },
      onSuccess: () => {
         void queryClient.invalidateQueries({ queryKey: guideEditQueryKey(guideid) });
      },
   });
}

export function useStepOrder(guideid: number) {
   const queryClient = useQueryClient();
   return useMutation({
      mutationFn: async (args: { revisionid: string; stepids: number[] }) => {
         const response = await apiClient.put(
            `guides/${guideid}/steporder?revisionid=${encodeURIComponent(args.revisionid)}`,
            { stepids: args.stepids }
         );
         return response.data;
      },
      // Returned (not voided) so the mutation stays pending until the
      // refetch lands: the "Save step order" button hides once the fresh
      // steps confirm the staged order, and isPending dropping earlier
      // flashed the button back to its idle label for the gap in between.
      onSuccess: () => queryClient.invalidateQueries({ queryKey: guideEditQueryKey(guideid) }),
      // The usual failure is a stale guide revisionid (reorder right
      // after add/delete/reorder). Also awaited: the retry only succeeds
      // with the refetched revisionid, so the button stays disabled until
      // clicking it again can work.
      onError: () => queryClient.invalidateQueries({ queryKey: guideEditQueryKey(guideid) }),
   });
}

export type GuideTeam = {
   teamid: number;
   name: string;
   image?: { mini?: string } | null;
};

export type GuideUser = {
   userid: number;
   username: string;
   unique_username?: string | null;
   image?: { mini?: string } | null;
};

/**
 * The private-guide allowlists. Adds and removes apply immediately through
 * their own endpoints (there is no bulk form), and every response carries
 * the full updated list, so mutations reseed the query from the response.
 */
export function useGuideTeams(guideid: number, enabled: boolean) {
   return useQuery({
      queryKey: ['guide-edit-teams', guideid] as const,
      queryFn: async () => {
         const response = await apiClient.get(`guides/${guideid}/teams`);
         return (response.data?.guideTeams ?? []) as GuideTeam[];
      },
      enabled,
      refetchOnWindowFocus: false,
   });
}

export function useGuideUsers(guideid: number, enabled: boolean) {
   return useQuery({
      queryKey: ['guide-edit-users', guideid] as const,
      queryFn: async () => {
         const response = await apiClient.get(`guides/${guideid}/users`);
         return (response.data?.users ?? []) as GuideUser[];
      },
      enabled,
      refetchOnWindowFocus: false,
   });
}

/**
 * The image of a device (topic) wiki. The caller names the size, because the
 * two places that want one draw it at very different scales: the picture
 * inside the device field is 37px wide, while the same device stands in for
 * the guide's own main image at the full width of that column. `enabled`
 * lets a caller skip the request entirely when it already knows it will not
 * draw the result. Empty names are skipped; a topic with no image resolves
 * to null. Cached indefinitely — a device's image rarely changes mid-edit.
 */
export function useTopicImage(topicName: string, size: 'thumbnail' | 'large', enabled = true) {
   const trimmed = topicName.trim();
   return useQuery({
      queryKey: ['topic-image', size, trimmed] as const,
      queryFn: async () => {
         const response = await apiClient.get<{ images?: Record<string, string> }>(
            `wikis/topic_images?size=${size}&t[]=${encodeURIComponent(trimmed)}`
         );
         // The response keys by the resolved title, which may differ from
         // the query (slug/case), so read the single value rather than by
         // name.
         return Object.values(response.data?.images ?? {})[0] ?? null;
      },
      enabled: enabled && trimmed !== '',
      staleTime: Number.POSITIVE_INFINITY,
      refetchOnWindowFocus: false,
   });
}

/**
 * Whether a device name is an existing category, via an authoritative CATEGORY
 * wiki lookup. The suggest index can't answer this: it's a ranked typeahead
 * that drops the exact term (searching "iPhone 15" returns only "iPhone 15
 * Pro/Plus"), so a real category never matches its own results. Only a 404
 * counts as "doesn't exist"; any other failure errs on "exists" so a network
 * blip never flags a real category as new, and an empty name resolves to true
 * so a blank field shows no indicator.
 */
export async function topicExists(name: string): Promise<boolean> {
   const trimmed = name.trim();
   if (trimmed === '') {
      return true;
   }
   try {
      await apiClient.get(`wikis/CATEGORY/${encodeURIComponent(trimmed)}`);
      return true;
   } catch (error) {
      const status = (error as { response?: { status?: number } }).response?.status;
      return status !== 404;
   }
}

export function useTopicExists(name: string) {
   const trimmed = name.trim();
   return useQuery({
      queryKey: ['topic-exists', trimmed.toLowerCase()] as const,
      queryFn: () => topicExists(trimmed),
      enabled: trimmed !== '',
      staleTime: Number.POSITIVE_INFINITY,
      refetchOnWindowFocus: false,
   });
}

export function useEditGuideTeam(guideid: number) {
   const queryClient = useQueryClient();
   return useMutation({
      mutationFn: async (args: { teamid: number; action: 'add' | 'remove' }) => {
         const path = `guides/${guideid}/teams/${args.teamid}`;
         const response =
            args.action === 'add' ? await apiClient.put(path) : await apiClient.delete(path);
         return (response.data?.guideTeams ?? []) as GuideTeam[];
      },
      onSuccess: teams => {
         queryClient.setQueryData(['guide-edit-teams', guideid], teams);
      },
   });
}

export function useEditGuideUser(guideid: number) {
   const queryClient = useQueryClient();
   return useMutation({
      mutationFn: async (args: { userid: number; action: 'add' | 'remove' }) => {
         const path = `guides/${guideid}/users/${args.userid}`;
         const response =
            args.action === 'add' ? await apiClient.put(path) : await apiClient.delete(path);
         return (response.data?.users ?? []) as GuideUser[];
      },
      onSuccess: users => {
         queryClient.setQueryData(['guide-edit-users', guideid], users);
      },
   });
}

/**
 * Parses intro/conclusion wikitext into the ProseMirror doc the WYSIWYG
 * editor mounts, with the same GuideIntro parser a pageload uses. Powers
 * the source -> visual mode switch in useWikitextEditor.
 */
export async function guideTextToJson(contents: string): Promise<unknown> {
   const response = await callAjaxIO<{ json: unknown }>('guideTextToJson', contents);
   return response.json;
}

/**
 * Renders one line's wikitext into HTML with the same GuideStep parser the
 * published guide uses, for the read-mode line previews.
 */
export function useRenderLine() {
   return useMutation({
      mutationFn: (text: string) => callAjaxIO<string>('getRendered', text),
   });
}

/** An empty line's doc, exactly as the server's Json renderer emits it. */
const EMPTY_LINE_DOC = { type: 'doc', content: [{ type: 'paragraph' }] };

export const lineDocQueryKey = (text: string) => ['step-line-doc', text] as const;

/**
 * The doc for a given source never changes, so entries live until evicted;
 * the editor also seeds the cache with its own doc for the text it
 * serialized, making re-opens instant.
 */
export function lineDocQueryOptions(text: string) {
   return {
      queryKey: lineDocQueryKey(text),
      queryFn: async () => {
         const response = await callAjaxIO<{ json: unknown }>('stepLineToJson', text);
         return response.json;
      },
      staleTime: Number.POSITIVE_INFINITY,
   };
}

/**
 * One line's wikitext parsed into the ProseMirror doc the WYSIWYG line
 * editor mounts, via the server's GuideStep parser — the editor never
 * parses wikitext itself; only doc -> wikitext serialization happens client
 * side.
 */
export function useLineDoc(text: string) {
   return useQuery({
      ...lineDocQueryOptions(text),
      // A blank line needs no round trip; its doc shape is fixed.
      initialData: text.trim() === '' ? EMPTY_LINE_DOC : undefined,
      refetchOnWindowFocus: false,
   });
}
